/**
 * Linked PDPs — "other product" colour swatches.
 *
 * Renders swatches that link to sibling products sharing a `product-family:*`
 * tag. Data is pulled from the `?view=json` collection endpoint
 * (sections/collection--json.liquid). Ported from the Dawn theme's
 * product-info.js `_initRelatedColors`, rebuilt as a standalone custom element
 * for Symmetry.
 *
 * Expected markup:
 *   <related-colors class="product-related-colors hidden"
 *     data-handle="{{ product.handle }}"
 *     data-tag="{{ related_tag }}"
 *     data-collection="all-products"
 *     data-label-target=".product__variant-color">
 *     ...current swatch placeholder...
 *   </related-colors>
 */
class RelatedColors extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;
    this.init();
  }

  async init() {
    this.currentHandle = this.getAttribute('data-handle');
    this.relatedTag = this.getAttribute('data-tag');
    this.collection = this.getAttribute('data-collection') || 'all-products';

    if (!this.relatedTag) return;

    const targetURL = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}collections/${this.collection}/${this.relatedTag}/?view=json`;

    let products;
    try {
      products = await this.fetchJSON(targetURL);
    } catch (err) {
      console.error('[RelatedColors] Failed to load related colours:', err);
      return;
    }

    if (!Array.isArray(products) || products.length <= 1) return;

    let markup = '';
    products.forEach((product) => {
      if (product.handle === this.currentHandle) {
        markup += `<div class="product-related-colors__current" aria-current="true" aria-label="${product.color}"><span class="product-related-colors__swatch" data-swatch="${product.swatch_color || ''}"></span></div>`;
      } else {
        markup += `<a href="${product.url}" class="product-related-colors__related" aria-label="${product.color}"><span class="product-related-colors__swatch" data-swatch="${product.swatch_color || ''}" js-color-swatch-link></span></a>`;
      }
    });

    this.innerHTML = markup;
    this.classList.remove('hidden');

    this.querySelectorAll('.product-related-colors__swatch').forEach((swatch) => {
      const color = swatch.getAttribute('data-swatch');
      if (color) swatch.style.backgroundColor = color;
    });

    this.bindHover();
  }

  bindHover() {
    const labelTarget = this.getAttribute('data-label-target');
    const labelEl = labelTarget ? document.querySelector(labelTarget) : null;
    if (!labelEl) return;

    const currentEl = this.querySelector('.product-related-colors__current');
    const defaultLabel = currentEl ? currentEl.getAttribute('aria-label') : labelEl.innerText;

    this.addEventListener('mouseover', (event) => {
      const target = event.target.closest('.product-related-colors__related');
      if (target) labelEl.innerText = target.getAttribute('aria-label');
    });

    this.addEventListener('mouseout', (event) => {
      if (event.target.closest('.product-related-colors__related')) {
        labelEl.innerText = defaultLabel;
      }
    });
  }

  fetchJSON(url) {
    return fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const parsedHTML = new DOMParser().parseFromString(text, 'text/html');
        const container = parsedHTML.querySelector('[js-collection-json]');
        if (!container) return [];
        return JSON.parse(container.innerHTML);
      });
  }
}

if (!customElements.get('related-colors')) {
  customElements.define('related-colors', RelatedColors);
}
